import {
  ComplianceCaseTypeEnum,
  ComplianceObjectType as ComplianceObjectTypeEnum,
  ProfileRelationshipEnum,
} from "#graphql/supergraphEnums";
import {
  ComplianceCaseTypeEnum as ComplianceCaseType,
  ComplianceObjectType,
  AccountTypeEnum,
  ProfileRelationshipEnum as ProfileRelationship,
} from "#graphql/supergraphTypes";
import { isJoinAccountType } from "#utils/account_types";
import { MISSING_VALUE_PLACEHOLDER } from "../common/constants";
import { ProfileTreeNode, InvestingProfile, TreeProfile } from "./types";

/**
 * Extracts profiles that have parent relationships of specific types.
 *
 * This function filters profiles based on their relationship types, keeping only those
 * that have at least one parent relationship marked as "owner", "controller", or "payment_beneficiary".
 *
 * @param profiles - Array of profiles to filter
 * @returns Array of filtered profiles with relevant parent relationships
 */
export const extractChildProfiles = (profiles: TreeProfile[]): TreeProfile[] => {
  if (!profiles || !profiles.length) return [];

  return profiles.filter((p) =>
    p?.parentRelationships?.some((pr) =>
      Object.values(ProfileRelationshipEnum).includes(
        pr.relationshipType as ProfileRelationshipEnum,
      ),
    ),
  );
};

/**
 * Builds a hierarchical tree structure from flat profiles.
 *
 * This function transforms a flat list of profiles into a tree structure using a two-pass approach:
 * 1. First pass: Create all nodes and store them in a map for efficient lookup
 * 2. Second pass: Establish parent-child relationships between nodes
 *
 * The investing profile serves as the root of the tree, and other profiles
 * are connected based on their parent relationships.
 *
 * @param profiles - Array of profiles to organize into a hierarchy
 * @param investingProfile - The root profile for the hierarchy
 * @returns A hierarchical tree structure with nested children
 */
export const buildHierarchy = (
  profiles: TreeProfile[],
  investingProfile: InvestingProfile,
): ProfileTreeNode => {
  // Initialize the root node using the investing profile
  const rootNode: ProfileTreeNode = {
    id: investingProfile.id,
    profileInfo: investingProfile,
    children: [],
  };

  // Create a map for fast node lookup by ID
  // This avoids O(n) searches when establishing relationships
  const nodeMap = new Map<number, ProfileTreeNode>();
  nodeMap.set(rootNode.id, rootNode);

  // FIRST PASS: Pre-process all profiles and create nodes
  // Create profile nodes with all necessary data and add them to the map
  profiles.forEach((profile) => {
    // Extract ownership percentage from parent relationships
    const percentageOwnership = profile.parentRelationships?.find(
      (pr) => pr.relationshipType == ProfileRelationshipEnum.Owner,
    )?.percentOwnership;

    // Create the node and add it to the map
    nodeMap.set(profile.id, {
      id: profile.id,
      parentId: profile.parentRelationships[0]?.parentProfileId,
      percentageOwnership: percentageOwnership ?? null,
      profileInfo: profile,
      children: [],
    });
  });

  // SECOND PASS: Establish parent-child relationships
  // Connect each node to its parent by adding it to the parent's children array
  [...nodeMap.values()].forEach((profile) => {
    if (profile.parentId) {
      const parentNode = nodeMap.get(profile.parentId);
      if (parentNode) {
        parentNode.children.push(profile);
      }
    }
  });

  // THIRD PASS: Calculate ownership shares for all nodes
  // Call our utility function to calculate root percentage ownership for all nodes
  calculateOwnershipShares(rootNode.children);

  // Return the fully constructed hierarchy with the root node at the top
  return rootNode;
};

type OwnershipNode = {
  percentageOwnership?: number | null;
  rootPercentageOwnership?: number | null;
  children: OwnershipNode[];
};

/**
 * Calculates the root percentage ownership for all nodes in a tree.
 *
 * Traverses the hierarchy and calculates the absolute percentage ownership
 * for each node relative to the root. The percentage is accumulated through
 * the hierarchy by multiplying the parent's accumulated share by the node's
 * direct percentage ownership.
 *
 * @param nodes - Array of hierarchical nodes to process
 * @param accumulatedShare - Starting share percentage (defaults to 100%)
 */
export const calculateOwnershipShares = <T extends OwnershipNode>(
  nodes: T[],
  accumulatedShare: number = 100,
): void => {
  const traverse = (node: T, currentAccumulatedShare: number): void => {
    if (!node.percentageOwnership) {
      node.rootPercentageOwnership = undefined;
      return;
    }

    const currentShare = currentAccumulatedShare * (node.percentageOwnership / 100);
    node.rootPercentageOwnership = currentShare;

    node.children?.forEach((child) => {
      traverse(child as T, currentShare);
    });
  };

  nodes.forEach((node) => {
    traverse(node, accumulatedShare);
  });
};

/**
 * Formats a person's full name from individual name components.
 *
 * Combines firstName, middleName, and lastName into a properly formatted full name.
 * If firstName or lastName is missing or empty, returns a placeholder value instead.
 * Handles null/undefined values and trims whitespace from all name parts.
 *
 * @param profile - Object containing name components (firstName, lastName, middleName)
 * @param missingValuePlaceholder - Value to return if required name parts are missing (default: "--")
 * @returns Formatted full name or placeholder if required fields are missing
 */
export const provideFullName = (
  profile: Pick<TreeProfile, "firstName" | "lastName" | "middleName"> | null | undefined,
): string => {
  if (!profile) return MISSING_VALUE_PLACEHOLDER;

  const { firstName, lastName, middleName } = profile;
  // Check for missing required fields
  if (!firstName?.trim() || !lastName?.trim()) {
    return MISSING_VALUE_PLACEHOLDER;
  }

  // Build the full name with proper spacing
  const parts = [firstName.trim(), middleName?.trim(), lastName.trim()]
    // filter(Boolean) removes any falsy values (null, undefined, empty strings)
    // This automatically handles missing middle names without extra conditionals
    .filter(Boolean);

  return parts.join(" ");
};

/**
 * Provides the legal name for a profile based on its compliance object type.
 *
 * For institutions and joint accounts, returns the legal name.
 * For individuals, returns the formatted full name.
 * Handles null/undefined values appropriately.
 *
 * @param profile - The profile object containing name information
 * @returns The legal name or a placeholder if information is missing
 */
export const provideLegalName = (
  profile?: Pick<
    TreeProfile,
    "complianceObjectType" | "firstName" | "lastName" | "middleName" | "legalName"
  > | null,
): string => {
  if (!profile) return MISSING_VALUE_PLACEHOLDER;
  const { legalName, complianceObjectType } = profile;
  const displayName = legalName?.trim() ?? MISSING_VALUE_PLACEHOLDER;
  const usesLegalName =
    complianceObjectType === ComplianceObjectTypeEnum.Institution ||
    complianceObjectType === ComplianceObjectTypeEnum.Joint;
  return usesLegalName ? displayName : provideFullName(profile);
};

/**
 * This utility is used to provide a name for a profile. when we have a scope of switching the account types between individual , joint and institution.
 */
export const provideProfileName = (
  profile?: Pick<
    TreeProfile,
    "complianceObjectType" | "firstName" | "lastName" | "middleName" | "legalName"
  > | null,
): string => {
  if (!profile) return MISSING_VALUE_PLACEHOLDER;

  const { legalName, complianceObjectType } = profile;
  const displayName = legalName?.trim() ?? MISSING_VALUE_PLACEHOLDER;
  const fullName = provideFullName(profile);

  if (complianceObjectType === ComplianceObjectTypeEnum.Institution) {
    return displayName !== MISSING_VALUE_PLACEHOLDER ? displayName : fullName;
  }

  return fullName !== MISSING_VALUE_PLACEHOLDER ? fullName : displayName;
};

/**
 * Gets a human-readable label for a compliance object type.
 *
 * @param complianceObjectType - The compliance object type enum value
 * @returns The human-readable label or undefined for unknown types
 */
export const getComplianceLabel = (
  complianceObjectType?: ComplianceObjectType | null,
  accountType?: AccountTypeEnum | null,
): string | undefined => {
  if (isJoinAccountType(accountType)) {
    return "Joint ownership account";
  }
  if (!complianceObjectType) return undefined;

  switch (complianceObjectType) {
    case ComplianceObjectTypeEnum.Institution:
      return "Institution";
    case ComplianceObjectTypeEnum.Individual:
      return "Individual";
    case ComplianceObjectTypeEnum.Joint:
      return "Joint ownership account";
    default:
      return undefined;
  }
};

/**
 * Gets a human-readable label for a relationship type.
 *
 * @param relationship - The relationship enum value
 * @returns The human-readable label or undefined for unknown types
 */
export const getRelationshipLabel = (
  relationship?: ProfileRelationship | null,
): string | undefined => {
  if (!relationship) return undefined;

  switch (relationship) {
    case ProfileRelationshipEnum.Owner:
      return "Owner";
    case ProfileRelationshipEnum.Controller:
      return "Controller";
    default:
      return undefined;
  }
};

export const pageSummary = (caseType: ComplianceCaseType, orgName: string) => {
  if (caseType == ComplianceCaseTypeEnum.ProfileRefresh) {
    return "Please verify the information is up to date and complete. Any modifications you apply will be reflected across all associated investments.";
  } else {
    return `Overview of owners and controllers of ${orgName} and its subsidiaries.`;
  }
};
